import type { PlanShipment } from '@api/distribution';

import { showError, showSuccess, showWarning } from './notifications';

/**
 * Decide the WMS-push toast purely from post-push shipment status — never from the HTTP promise.
 *
 * Why: the push endpoint returns 200 regardless of the WMS result, and the request is synchronous and
 * slow enough to hit a gateway timeout even when the backend succeeds. So the only reliable signal is
 * the shipment's `wmsStatus` after the fact. The rule (agreed): a shipment that reached `CREATED` is a
 * success — even if the request 504'd, or Unicommerce rejected a duplicate (40001) that the backend
 * reconciled to CREATED. A shipment is atomic (all its transfers flip together), so partial only ever
 * means *across* shipments.
 *
 * @param successPrefix optional lead-in (e.g. 'Plan approved') used in the success/partial/error copy.
 * @returns the verdict so callers can drive UI phase (e.g. stay on retry vs. show done).
 */
export function reportWmsPushOutcome(
    shipments: PlanShipment[],
    attemptedCodes: Set<string>,
    successPrefix?: string,
): 'success' | 'partial' | 'error' {
    const attempted = shipments.filter(s => attemptedCodes.has(s.saleOrderCode));
    const created = attempted.filter(s => s.wmsStatus === 'CREATED');
    const failed = attempted.filter(s => s.wmsStatus !== 'CREATED'); // FAILED, or never pushed (null)

    const lead = successPrefix ? `${successPrefix} · ` : '';

    if (attempted.length === 0 || failed.length === 0) {
        const n = created.length;
        showSuccess(`${lead}${n} sale order${n !== 1 ? 's' : ''} created in WMS`.trim());
        return 'success';
    }

    const reasons = failed
        .map(s => `${s.saleOrderName ?? s.saleOrderCode}: ${s.wmsErrorMessage ?? 'unknown error'}`)
        .join('; ');

    if (created.length > 0) {
        showWarning(
            `${lead}${created.length} of ${attempted.length} sale orders created. ${failed.length} failed — ${reasons}`,
            'Partial success',
            8000,
        );
        return 'partial';
    }

    const errLead = successPrefix ? `${successPrefix}, but ` : '';
    showError(
        `${errLead}${failed.length} sale order${failed.length !== 1 ? 's' : ''} failed — ${reasons}`,
        'Error',
        8000,
    );
    return 'error';
}
